import React from 'react'
import DataRowHeader from '../../../../../../components/Schedule/data/row-header';
import {
    TimeSheetGroupType,
    TimeSheetItem,
    UpdateStatusAbility
} from '../../../../../../features/schedule/types';
import {GridGroupByProps} from '../../../../../../components/shared/grid/types';
import {usePlannedHours} from '../planned-hours';
import {useAppSelector} from '../../../../../../hooks/hooks';
import {IntlShape} from 'react-intl';
import {
    getTimesheetGridTotals,
    getHoursToApprove,
    getHoursToBill
} from '../../../../../../helpers/timesheet-summary-hours';

interface DateRange {
    startDate: string;
    endDate: string;
}

type GroupField = 'date' | 'targetName' | 'employeeName';

const getRangeAndEmployee = (
    items: TimeSheetItem[],
    fallback?: DateRange | null,
    fallbackEmployeeId?: number | null
) => {
    // if(groupType != TimeSheetGroupType.EMPLOYEEVIEW)
    //     return null;

    // Assumes same employee per group of items at the level where we render headers.
    const employeeIdFromItems =
        items.find((item) => (item?.employeeId ?? 0) > 0)?.employeeId ?? items[0]?.employeeId;
    const employeeId = employeeIdFromItems ?? fallbackEmployeeId ?? null;

    if(!employeeId || !fallback?.startDate || !fallback?.endDate) { return null}
    const startDate = fallback?.startDate;
    const endDate = fallback?.endDate;
    return { employeeId, startDate, endDate };
};

// Wrapper to fetch planned hours for the group and render the header.
const GroupHeader: React.FC<{
    groupField: 'date' | 'targetName' | 'employeeName';
    groupFieldValue: string;
    timeshifts: TimeSheetItem[];
    intl: IntlShape;
    templateColumns: string;
    level?: number;
    approveAbility: UpdateStatusAbility;
    billalbeAbility: UpdateStatusAbility;
    onApproveAllClick: () => Promise<void>;
    onBillAllClick: () => Promise<void>;
    onDisapproveAllClick: () => Promise<void>;
    onCancelAllBillingClick: () => Promise<void>;
    onAddNewShiftClick: () => Promise<void>;
    fallbackRange?: DateRange | null;
    summaryColumnIndexes?: Record<string, number>;
    collapseColumnEnd?: number;
    canManageEntry?: (item: TimeSheetItem) => boolean;
    disabled?: boolean;
}> = ({
    groupField,
    groupFieldValue,
    timeshifts,
    intl,
    level,
    approveAbility,
    billalbeAbility,
    onApproveAllClick,
    templateColumns,
    onBillAllClick,
    onDisapproveAllClick,
    onCancelAllBillingClick,
    onAddNewShiftClick,
    fallbackRange,
    summaryColumnIndexes,
    collapseColumnEnd,
    canManageEntry,
    disabled,
}) => {
    const employees = useAppSelector((selector) => selector.rEmployees);
    const totals = React.useMemo(() => getTimesheetGridTotals(timeshifts), [timeshifts]);
    const billableCandidates = React.useMemo(
        () => getHoursToBill(timeshifts),
        [timeshifts]
    );
    const fallbackEmployeeId = React.useMemo<number | null>(() => {
        if (groupField !== 'employeeName') {
            return null;
        }
        // Allows planned hours fetch even when a group has no concrete timesheet rows.
        return employees.find((employee) => employee.name === groupFieldValue)?.id ?? null;
    }, [employees, groupField, groupFieldValue]);

    // Derive params; if no dates or employee, skip fetch.
    const params = React.useMemo(
        () => getRangeAndEmployee(
            timeshifts,
            groupField === 'employeeName' ? fallbackRange : null,
            fallbackEmployeeId
        ),
        [fallbackEmployeeId, fallbackRange?.endDate, fallbackRange?.startDate, groupField, timeshifts]
    );
    const { plannedHours, isLoading } = usePlannedHours(params);

    // Fallback: if BE not available yet, show 0 to avoid noisy diffs.
    const planned = isLoading || !params ? 0 : plannedHours;

    return (
        <DataRowHeader
            name={groupFieldValue}
            total={totals.total}
            approved={totals.approved}
            approvableHours={totals.approvable}
            billed={totals.billed}
            approvable={getHoursToApprove(timeshifts)}
            billable={billableCandidates}
            billableHours={totals.billable}
            workHours={totals.workHours}
            absenceHours={totals.absenceHours}
            plannedHours={planned}
            hideTrackingHours={groupField === 'targetName'}
            approveAbility={approveAbility}
            billalbeAbility={billalbeAbility}
            onApproveAllClick={onApproveAllClick}
            onBillAllClick={onBillAllClick}
            onDisapproveAllClick={onDisapproveAllClick}
            onCancelAllBillingClick={onCancelAllBillingClick}
            onAddNewShiftClick={onAddNewShiftClick}
            hideAddAction={timeshifts[0] ? !canManageEntry?.(timeshifts[0]) : true}
            hideButtons={Boolean(disabled) || (level !== undefined && level > 1)}
            templateColumns={templateColumns}
            summaryColumnIndexes={summaryColumnIndexes}
            collapseColumnEnd={collapseColumnEnd}
            intl={intl}
        />
    );
};

const useGridGroupBy = (
    groupType: TimeSheetGroupType,
    handleApproveAllClick: (timeshifts: Array<TimeSheetItem>) => Promise<void>,
    handleBillAllClick: (timeshifts: Array<TimeSheetItem>) => Promise<void>,
    handleDisapproveAllClick: (timeshift: Array<TimeSheetItem>) => Promise<void>,
    handleCancelAllBillingClick: (timeshift: Array<TimeSheetItem>) => Promise<void>,
    onAddNewShiftAddClick: (timeshift: TimeSheetItem) => Promise<void>,
    approveAbility: UpdateStatusAbility,
    billalbeAbility: UpdateStatusAbility,
    canManageEntry: (item: TimeSheetItem) => boolean,
    templateColumns: string,
    fallbackRange?: DateRange | null,
    summaryColumnIndexes?: Record<string, number>,
    collapseColumnEnd?: number,
    disabled?: boolean
): GridGroupByProps => React.useMemo(() => ({
    rowGroupingModel: groupType === TimeSheetGroupType.EMPLOYEEVIEW
        ? ['employeeName', 'date']
        : ['targetName', 'employeeName'],
    groupByRenderer: (groupField, groupFieldValue, value, intl: IntlShape, level) => {
        switch (groupField) {
            case  'date':
            case 'targetName':
            case 'employeeName': {
                const timeshifts = value as Array<TimeSheetItem>;
                return (
                    <GroupHeader
                        groupField={groupField as GroupField}
                        groupFieldValue={groupFieldValue}
                        timeshifts={timeshifts}
                        intl={intl}
                        level={level}
                        approveAbility={approveAbility}
                        billalbeAbility={billalbeAbility}
                        onApproveAllClick={async () => await handleApproveAllClick(timeshifts)}
                        onBillAllClick={async () => await handleBillAllClick(timeshifts)}
                        onDisapproveAllClick={async () => await handleDisapproveAllClick(timeshifts)}
                        onCancelAllBillingClick={async () => await handleCancelAllBillingClick(timeshifts)}
                        onAddNewShiftClick={async () => {
                            if (disabled || !timeshifts[0] || !canManageEntry(timeshifts[0])) {
                                return;
                            }
                            await onAddNewShiftAddClick({ ...timeshifts[0], date: '' });
                        }}
                        fallbackRange={fallbackRange}
                        templateColumns={templateColumns}
                        summaryColumnIndexes={summaryColumnIndexes}
                        collapseColumnEnd={collapseColumnEnd}
                        canManageEntry={canManageEntry}
                        disabled={disabled}
                    />)
            }

            default:
                return (<></>)
        }
    },
    isGroupHasValues: (values) => {
        return values.find((x) => (x as TimeSheetItem).hours > 0) !== undefined;
    }
}), [
    fallbackRange?.endDate,
    fallbackRange?.startDate,
    groupType,
    handleApproveAllClick,
    handleBillAllClick,
    handleDisapproveAllClick,
    handleCancelAllBillingClick,
    onAddNewShiftAddClick,
    canManageEntry,
    templateColumns,
    summaryColumnIndexes,
    collapseColumnEnd,
    disabled,
])

export default useGridGroupBy;
